import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import api from '../lib/api';
import { getPreviewUrl, revokePreviewUrl } from '../lib/documentPreview';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Users, 
  Search, 
  Eye,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Filter,
  RefreshCw,
  Building2,
  Download,
  MessageCircle
} from 'lucide-react';

// Component for employee avatar with profile photo support
const EmployeeAvatar = ({ employee, size = "h-10 w-10" }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const loadPhoto = async () => {
      if (employee.profile_photo_id && !photoUrl) {
        try {
          setLoading(true);
          // Şub 27, 2026 — Direct CDN URL ile anında yükleme
          const { url } = await getPreviewUrl(api, employee.profile_photo_id, true);
          if (url) setPhotoUrl(url);
        } catch (error) {
          console.error('Error loading profile photo:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadPhoto();
    
    return () => {
      revokePreviewUrl(photoUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.profile_photo_id]);
  
  const initial = (employee.profile?.voornaam || employee.user?.first_name || employee.user?.full_name)?.[0]?.toUpperCase() || 'U';
  
  if (loading) {
    return (
      <div className={`${size} rounded-full bg-slate-200 flex items-center justify-center`}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (photoUrl) {
    return (
      <img 
        src={photoUrl} 
        alt="Profile" 
        className={`${size} rounded-full object-cover border-2 border-white shadow-sm`}
      />
    );
  }
  
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium`}>
      {initial}
    </div>
  );
};

export default function EmployeeList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read URL params synchronously for correct initial state on direct
  // navigation (useSearchParams can be empty at first render).
  const initialParams = new URLSearchParams(window.location.search);

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  // 'none' (default — backend ordering) → 'asc' (A→Z) → 'desc' (Z→A) → 'none'
  const [nameSort, setNameSort] = useState('none');

  // Filters — initialized from URL so returning via browser-back or a direct
  // link restores the exact list state (page, filters, search).
  const [searchQuery, setSearchQuery] = useState(() => initialParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => initialParams.get('q') || '');
  const [profileStatus, setProfileStatus] = useState(() => initialParams.get('profile') || 'all');
  const [userStatus, setUserStatus] = useState(() => initialParams.get('status') || 'ACTIVE');
  const [firmFilter, setFirmFilter] = useState(() => initialParams.get('firm') || '');
  const [firms, setFirms] = useState([]);

  // Pagination (also URL-backed)
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(initialParams.get('page'), 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const s = parseInt(initialParams.get('size'), 10);
    return Number.isFinite(s) && s > 0 ? s : 10;
  });

  // Track the last visited employee so we can highlight their row and show a
  // contextual toast when the admin returns via the Back button. Stored as
  // JSON `{id, label}` in sessionStorage — ephemeral (clears on tab close).
  const readLastVisited = () => {
    try {
      const raw = sessionStorage.getItem('emp-last-visited');
      if (!raw) return null;
      // Back-compat: older values may be a bare id string.
      if (raw.startsWith('{')) return JSON.parse(raw);
      return { id: raw, label: '' };
    } catch { return null; }
  };
  const [lastVisitedId, setLastVisitedId] = useState(() => readLastVisited()?.id || null);
  // Fire the "Son görüntülenen" toast exactly once per return (mount).
  const toastFiredRef = useRef(false);

  // Navigate to employee profile — persist the clicked row id+label first so
  // we can scroll+highlight and toast when the user returns.
  const handleRowClick = (emp) => {
    const id = emp?.user?.id;
    if (!id) return;
    const label = `${emp?.profile?.voornaam || emp?.user?.first_name || ''} ${emp?.profile?.naam || emp?.user?.last_name || emp?.user?.full_name || ''}`.trim() || (emp?.user?.email || '');
    try { sessionStorage.setItem('emp-last-visited', JSON.stringify({ id, label })); } catch {}
    setLastVisitedId(id);
    toastFiredRef.current = false; // re-arm for the return trip
    navigate(`/dashboard/employees/${id}`);
  };

  // On mount: if returning from a profile, surface a short contextual toast
  // so the user sees the full name before the scroll+highlight animation
  // finishes. Runs once per mount.
  useEffect(() => {
    if (toastFiredRef.current) return;
    const last = readLastVisited();
    if (last?.id && last?.label) {
      toast.info(`Son görüntülenen: ${last.label}`, {
        duration: 10000,
        action: {
          label: 'Tekrar Aç',
          onClick: () => navigate(`/dashboard/employees/${last.id}`),
        },
        cancel: {
          label: 'Kapat',
          onClick: () => {},
        },
        classNames: {
          cancelButton: '!bg-red-500 !text-white hover:!bg-red-600',
        },
      });
    }
    toastFiredRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After the list renders, scroll the previously visited row into view and
  // fade the highlight after ~2.5s. Runs whenever the employees array or the
  // target id changes (handles paginated fetches).
  useEffect(() => {
    if (!lastVisitedId || employees.length === 0) return;
    const el = document.querySelector(`[data-row-id="${lastVisitedId}"]`);
    if (!el) return;
    // Defer slightly so layout is settled (avatars/photos may async-load).
    const rafId = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => {
      setLastVisitedId(null);
      try { sessionStorage.removeItem('emp-last-visited'); } catch {}
    }, 2500);
    return () => { cancelAnimationFrame(rafId); clearTimeout(timer); };
  }, [employees, lastVisitedId]);

  // Sync filters/pagination back to URL so navigation preserves state without
  // polluting history (replace:true keeps a single entry for the list view).
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set('q', debouncedSearch);
    if (profileStatus !== 'all') next.set('profile', profileStatus);
    if (userStatus !== 'ACTIVE') next.set('status', userStatus);
    if (firmFilter) next.set('firm', firmFilter);
    if (currentPage !== 1) next.set('page', String(currentPage));
    if (perPage !== 10) next.set('size', String(perPage));
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, profileStatus, userStatus, firmFilter, currentPage, perPage, setSearchParams]);

  // Debounce search — only reset page when the value actually changed by
  // user interaction. On mount both `searchQuery` and `debouncedSearch` are
  // initialized from the URL, so they're equal → no unintended page reset.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearch) {
        setDebouncedSearch(searchQuery);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    fetchEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, profileStatus, userStatus, firmFilter, debouncedSearch]);

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching firms:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const skip = (currentPage - 1) * perPage;
      params.append('skip', skip.toString());
      params.append('limit', perPage.toString());
      if (profileStatus !== 'all') params.append('profile_status', profileStatus);
      if (userStatus !== 'all') params.append('user_status', userStatus);
      if (firmFilter) params.append('firm_id', firmFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      
      const response = await api.get(`/employee-profile/list?${params.toString()}`);
      if (response.data.success) {
        setEmployees(response.data.data.employees);
        setTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(t('employeeProfile.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(total / perPage);

  // Safety clamp: if the URL-restored currentPage is beyond the available
  // pages (e.g. filter narrowed the result set since last visit), snap to
  // the last valid page so the table doesn't render empty on return.
  useEffect(() => {
    if (loading) return;
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [loading, totalPages, currentPage]);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (value) => {
    setPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page
  };

  const handleExportCSV = () => {
    // Create CSV content with i18n headers
    const headers = [
      t('employeeProfile.lastName'), 
      t('employeeProfile.firstName'), 
      t('employeeProfile.emailAddress'), 
      t('employeeProfile.phone'), 
      t('employeeProfile.profile') + ' %', 
      t('common.firm'), 
      t('common.status')
    ];
    const rows = employees.map(emp => [
      emp.profile?.naam || emp.user?.last_name || '',
      emp.profile?.voornaam || emp.user?.first_name || '',
      emp.user?.email || '',
      emp.profile?.gsm || emp.user?.phone || '',
      emp.profile_completion_percentage,
      emp.user?.firm_name || '',
      emp.profile_completed ? t('employeeProfile.complete') : t('employeeProfile.incomplete')
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `employees_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Statistics
  const completeCount = employees.filter(e => e.profile_completed).length;
  const incompleteCount = employees.filter(e => !e.profile_completed).length;
  const avgCompletion = employees.length > 0 
    ? Math.round(employees.reduce((sum, e) => sum + e.profile_completion_percentage, 0) / employees.length)
    : 0;

  // Sorted view of `employees` — applied client-side because the backend
  // already paginates with its own ordering. Switching back to 'none' keeps
  // the original sequence so the user can compare against legacy ordering.
  const displayedEmployees = useMemo(() => {
    if (nameSort === 'none') return employees;
    const dir = nameSort === 'asc' ? 1 : -1;
    const fullName = (emp) => `${emp.profile?.voornaam || emp.user?.first_name || ''} ${emp.profile?.naam || emp.user?.last_name || emp.user?.full_name || ''}`.trim();
    return [...employees].sort((a, b) => dir * fullName(a).localeCompare(fullName(b), 'tr'));
  }, [employees, nameSort]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Users className="h-7 w-7 text-blue-600" />
            {t('employeeProfile.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{total} {t('employeeProfile.employeesFound')}</p>
        </div>
        <Button variant="outline" onClick={handleExportCSV}>
          <Download className="h-4 w-4 mr-2" />
          {t('employeeProfile.exportCsv')}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('common.total')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completeCount}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.completeProfiles')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{incompleteCount}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.incompleteProfiles')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgCompletion}%</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('employeeProfile.avgCompletion')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('common.filter')}:</span>
            </div>
            <div className="flex-1 min-w-[200px] max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('employeeProfile.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={profileStatus} onValueChange={setProfileStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={t('employeeProfile.profile')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('employeeProfile.allProfiles')}</SelectItem>
                <SelectItem value="complete">{t('employeeProfile.complete')}</SelectItem>
                <SelectItem value="incomplete">{t('employeeProfile.incomplete')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={userStatus} onValueChange={(v) => { setUserStatus(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="filter-user-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="ACTIVE">Aktif</SelectItem>
                <SelectItem value="SUSPENDED">Askıda</SelectItem>
                <SelectItem value="PENDING">Bekleyen</SelectItem>
              </SelectContent>
            </Select>
            {firms.length > 0 && (
              <Select value={firmFilter || "all"} onValueChange={(v) => setFirmFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('employeeProfile.allFirms')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('employeeProfile.allFirms')}</SelectItem>
                  {firms.map(firm => (
                    <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setProfileStatus('all'); setUserStatus('ACTIVE'); setFirmFilter(''); setCurrentPage(1); }}>
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Per Page Selector & Pagination Info */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-1">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600 dark:text-slate-400">{t('common.show') || 'Göster'}:</span>
          <Select value={perPage.toString()} onValueChange={handlePerPageChange}>
            <SelectTrigger className="w-[80px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-600 dark:text-slate-400">{t('common.perPage') || 'kayıt'}</span>
        </div>
        <div className="text-sm text-slate-600 dark:text-slate-400">
          {t('common.showing') || 'Gösterilen'}: {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, total)} / {total} {t('common.total') || 'toplam'}
        </div>
      </div>

      {/* Employee Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('employeeProfile.noEmployees')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => setNameSort((p) => p === 'none' ? 'asc' : p === 'asc' ? 'desc' : 'none')}
                      className="inline-flex items-center gap-1 hover:text-emerald-600 font-semibold"
                      data-testid="employees-sort-name"
                      title="Çalışan adına göre sırala (A→Z, Z→A, varsayılan)"
                    >
                      {t('employeeProfile.employee')}
                      <span className="text-[10px] tabular-nums opacity-70">
                        {nameSort === 'asc' ? '▲ A→Z' : nameSort === 'desc' ? '▼ Z→A' : '↕'}
                      </span>
                    </button>
                  </TableHead>
                  <TableHead>{t('employeeProfile.contact')}</TableHead>
                  <TableHead>{t('common.firm')}</TableHead>
                  <TableHead className="text-center">{t('employeeProfile.employmentStartDate') || 'İşe Başlama'}</TableHead>
                  <TableHead className="text-center">{t('employeeProfile.employmentEndDate') || 'Ayrılma'}</TableHead>
                  <TableHead className="text-center">{t('employeeProfile.profile')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedEmployees.map((emp) => {
                  const isHighlighted = lastVisitedId && emp.user?.id === lastVisitedId;
                  return (
                  <TableRow
                    key={emp.user?.id}
                    data-row-id={emp.user?.id}
                    data-testid={`employee-row-${emp.user?.id}`}
                    className={`cursor-pointer transition-all duration-500 ${
                      isHighlighted
                        ? 'ring-2 ring-inset ring-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                    onClick={() => handleRowClick(emp)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar employee={emp} size="h-10 w-10" />
                        <div>
                          <p className="font-medium">
                            {emp.profile?.voornaam || emp.user?.first_name || ''} {emp.profile?.naam || emp.user?.last_name || emp.user?.full_name || ''}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{emp.user?.role}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{emp.user?.email}</p>
                        {(() => {
                          const raw = emp.profile?.gsm || emp.user?.phone || '';
                          const digits = String(raw).replace(/\D/g, '');
                          const canWA = digits.length >= 6;
                          return (
                            <div className="flex items-center gap-1.5">
                              <p className="text-slate-500 dark:text-slate-400">{raw || '-'}</p>
                              {canWA && (
                                <a
                                  href={`https://wa.me/${digits}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors"
                                  title="WhatsApp Web'de aç"
                                  data-testid={`employee-list-wa-${emp.user?.id}`}
                                >
                                  <MessageCircle className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        <span className="text-sm">{emp.user?.firm_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm">
                        {emp.user?.employment_start_date 
                          ? new Date(emp.user.employment_start_date).toLocaleDateString('tr-TR')
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm">
                        {emp.user?.employment_end_date 
                          ? new Date(emp.user.employment_end_date).toLocaleDateString('tr-TR')
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 justify-center">
                        <Progress value={emp.profile_completion_percentage} className="w-20 h-2" />
                        <span className="text-sm font-medium w-10">{emp.profile_completion_percentage}%</span>
                        {emp.profile_completed ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700">
                            <AlertCircle className="h-3 w-3 mr-1" /> !
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('common.page') || 'Sayfa'} {currentPage} / {totalPages}
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(1)}
            >
              «
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {t('common.previous') || 'Önceki'}
            </Button>
            
            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className="w-9"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {t('common.next') || 'Sonraki'}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
